import { TabsList } from "@/components/ui/tabs";
import { CurriculumParentStripNavSyncContext } from "@/contexts/CurriculumParentStripNavSyncContext";
import { CurriculumSubjectNavSyncContext } from "@/contexts/CurriculumSubjectNavSyncContext";
import { cn } from "@/lib/utils";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type FocusEvent,
  type ReactNode,
} from "react";

export interface ScrollableCurriculumTabStripProps {
  /** Typically `<TabsTrigger />` nodes — must stay inside `<Tabs>`. */
  children: ReactNode;
  /** Used in chevron `aria-label`s, e.g. "Theory sub-tabs". */
  ariaLabelPrefix?: string;
  className?: string;
  /** Merged with default `TabsList` classes. */
  tabsListClassName?: string;
}

export type ScrollableCurriculumTabStripHandle = {
  /** The horizontal scroll container (for parent-strip sync providers). */
  getScrollContainer: () => HTMLDivElement | null;
};

/**
 * Horizontal tab strip matching main curriculum nav: no wrap, hidden scrollbar,
 * grey chevron overlays when content overflows.
 */
export const ScrollableCurriculumTabStrip = forwardRef<
  ScrollableCurriculumTabStripHandle,
  ScrollableCurriculumTabStripProps
>(function ScrollableCurriculumTabStrip(
  { children, ariaLabelPrefix = "Scroll tabs", className, tabsListClassName },
  forwardedRef,
) {
  const navScrollRef = useRef<HTMLDivElement | null>(null);
  const [showLeftHint, setShowLeftHint] = useState(false);
  const [showRightHint, setShowRightHint] = useState(false);
  const syncParentOrchestratorStrip = useContext(CurriculumParentStripNavSyncContext);
  const syncSubjectRow = useContext(CurriculumSubjectNavSyncContext);
  const ancestorSyncRafRef = useRef<number | null>(null);

  useImperativeHandle(
    forwardedRef,
    () => ({
      getScrollContainer: () => navScrollRef.current,
    }),
    [],
  );

  const notifyAncestorStrips = useCallback(() => {
    if (!syncParentOrchestratorStrip && !syncSubjectRow) return;
    if (ancestorSyncRafRef.current != null) {
      cancelAnimationFrame(ancestorSyncRafRef.current);
    }
    ancestorSyncRafRef.current = requestAnimationFrame(() => {
      ancestorSyncRafRef.current = null;
      requestAnimationFrame(() => {
        syncParentOrchestratorStrip?.();
        syncSubjectRow?.();
      });
    });
  }, [syncParentOrchestratorStrip, syncSubjectRow]);

  const updateOverflowHints = useCallback(() => {
    const el = navScrollRef.current;
    if (!el) return;
    const { clientWidth, scrollWidth } = el;
    const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);
    if (el.scrollLeft > maxScrollLeft) {
      el.scrollLeft = maxScrollLeft;
    }
    setShowLeftHint(el.scrollLeft > 4);
    setShowRightHint(maxScrollLeft - el.scrollLeft > 4);
  }, []);

  const handleStripScroll = useCallback(() => {
    updateOverflowHints();
    notifyAncestorStrips();
  }, [notifyAncestorStrips, updateOverflowHints]);

  const scrollNavStrip = useCallback(
    (direction: "backward" | "forward") => {
      const el = navScrollRef.current;
      if (!el) return;
      const delta = Math.max(160, Math.round(el.clientWidth * 0.65));
      const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
      const nextLeft =
        direction === "backward"
          ? Math.max(0, el.scrollLeft - delta)
          : Math.min(maxScrollLeft, el.scrollLeft + delta);
      el.scrollTo({ left: nextLeft, behavior: "smooth" });
      notifyAncestorStrips();
    },
    [notifyAncestorStrips],
  );

  const handleStripClick = useCallback(() => {
    notifyAncestorStrips();
  }, [notifyAncestorStrips]);

  const handleStripFocusCapture = useCallback(
    (e: FocusEvent<HTMLDivElement>) => {
      const el = e.target as HTMLElement | null;
      if (el?.getAttribute?.("role") === "tab") {
        notifyAncestorStrips();
      }
    },
    [notifyAncestorStrips],
  );

  useEffect(() => {
    return () => {
      if (ancestorSyncRafRef.current != null) {
        cancelAnimationFrame(ancestorSyncRafRef.current);
      }
    };
  }, []);

  useEffect(() => {
    updateOverflowHints();
    window.addEventListener("resize", updateOverflowHints);
    return () => window.removeEventListener("resize", updateOverflowHints);
  }, [updateOverflowHints]);

  useLayoutEffect(() => {
    const el = navScrollRef.current;
    if (!el) return;
    updateOverflowHints();
    requestAnimationFrame(updateOverflowHints);
    const ro = new ResizeObserver(() => updateOverflowHints());
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateOverflowHints]);

  return (
    <div
      className={cn(
        "relative border-b bg-white/95 backdrop-blur md:border-0 md:bg-transparent",
        className,
      )}
      onClick={handleStripClick}
      onFocusCapture={handleStripFocusCapture}
    >
      <div
        ref={navScrollRef}
        onScroll={handleStripScroll}
        className="touch-pan-x overflow-x-auto px-2 py-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        <TabsList
          className={cn(
            "inline-flex h-auto w-max min-w-0 flex-nowrap justify-start gap-2 rounded-full bg-gray-100 p-1",
            tabsListClassName,
          )}
        >
          {children}
        </TabsList>
      </div>
      {showLeftHint && (
        <div className="pointer-events-none absolute inset-y-0 left-0 z-30 flex w-14 items-center justify-start bg-gradient-to-r from-white via-white/90 to-transparent pl-2">
          <button
            type="button"
            className="pointer-events-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white/95 shadow-sm ring-1 ring-white/70 transition hover:bg-white focus-visible:outline focus-visible:ring-2 focus-visible:ring-gray-400"
            aria-label={`${ariaLabelPrefix} left`}
            onClick={() => scrollNavStrip("backward")}
          >
            <ChevronsLeft className="h-5 w-5 text-gray-500" />
          </button>
        </div>
      )}
      {showRightHint && (
        <div className="pointer-events-none absolute inset-y-0 right-0 z-30 flex w-14 items-center justify-end bg-gradient-to-l from-white via-white/90 to-transparent pr-2">
          <button
            type="button"
            className="pointer-events-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white/95 shadow-sm ring-1 ring-white/70 transition hover:bg-white focus-visible:outline focus-visible:ring-2 focus-visible:ring-gray-400"
            aria-label={`${ariaLabelPrefix} right`}
            onClick={() => scrollNavStrip("forward")}
          >
            <ChevronsRight className="h-5 w-5 text-gray-500" />
          </button>
        </div>
      )}
    </div>
  );
});
